import React from 'react';
import {
    Phone, Mail, MapPin, Users, Building2, User, Crown, MessageCircle, UserCog, AlertTriangle, Loader2,
} from 'lucide-react';
import {
    LemitPessoaData, LemitEmpresaData, LemitTipo, LemitTelefone, LemitEmail, LemitEndereco, LemitSocio,
} from '../../../../types';

// ─────────────────────────────────────────────────────────────────────────────
// Painel de enriquecimento Lemit reutilizável (dados do lead + do sócio/dono).
// Usado no modal de detalhe da Lista Fria e na aba "Enriquecimento" do modal de
// negociação. Aceita tanto o retorno cru da Lemit (LemitPessoaData/EmpresaData)
// quanto o `enriquecimento_data` encenado da Lista Fria (via `enrToLemitLike`).
// `socioData` traz o que a Lemit extraiu do CPF do representante (seção própria).
// ─────────────────────────────────────────────────────────────────────────────

const fmtDoc = (doc?: string): string => {
    const c = (doc || '').replace(/\D/g, '');
    if (c.length === 11) return c.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (c.length === 14) return c.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return doc || '—';
};
const fmtPhone = (p: LemitTelefone): string => {
    const n = (p.numero || '').replace(/\D/g, '');
    const num = n.length >= 9 ? `${n.slice(0, n.length - 4)}-${n.slice(-4)}` : n;
    return `(${p.ddd ?? '--'}) ${num}`;
};
const fmtBRL = (v?: number | null) => v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
const fmtDate = (iso?: string) => {
    if (!iso) return '—';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const card = 'bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden';
const sectionHead = 'flex items-center gap-2.5 px-4 py-3 border-b border-slate-100 dark:border-slate-800';

export const SectionCard: React.FC<{ icon: React.ReactNode; title: string; count?: number; tint?: string; children: React.ReactNode }> =
    ({ icon, title, count, tint = 'text-primary bg-primary/8 dark:bg-primary/15', children }) => (
        <div className={card}>
            <div className={sectionHead}>
                <div className={`p-1.5 rounded-lg ${tint}`}>{icon}</div>
                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">{title}</h4>
                {count != null && count > 0 && (
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-primary/10 text-primary rounded-full">{count}</span>
                )}
            </div>
            <div className="p-4">{children}</div>
        </div>
    );

const InfoCell: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <div className="space-y-0.5">
        <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">{label}</p>
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 break-words">{value || '—'}</p>
    </div>
);

const EmptyRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p className="text-xs text-slate-400 italic py-2 text-center">{children}</p>
);

const waLink = (digits?: string) => digits ? `https://wa.me/55${(digits || '').replace(/\D/g, '')}` : undefined;

// ── Blocos de contato reutilizados pela empresa e pelo representante ───────────
const Telefones: React.FC<{ tels: LemitTelefone[] }> = ({ tels }) => {
    if (!tels.length) return <EmptyRow>Nenhum telefone retornado</EmptyRow>;
    return (
        <div className="flex flex-wrap gap-2">
            {tels.slice().sort((a, b) => (a.ranking ?? 99) - (b.ranking ?? 99)).map((p, i) => (
                <a key={i} href={p.whatsapp ? waLink(`${p.ddd ?? ''}${p.numero ?? ''}`) : undefined} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700 hover:border-emerald-300 transition-colors">
                    <span className="font-mono font-bold text-sm text-slate-700 dark:text-slate-200">{fmtPhone(p)}</span>
                    {p.whatsapp && <span className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded"><MessageCircle size={9} /> WhatsApp</span>}
                    {p.ranking === 1 && <span className="px-1.5 py-0.5 text-[9px] font-bold bg-primary/10 text-primary rounded">melhor</span>}
                </a>
            ))}
        </div>
    );
};

const Emails: React.FC<{ emails: LemitEmail[] }> = ({ emails }) => {
    if (!emails.length) return <EmptyRow>Nenhum e-mail retornado</EmptyRow>;
    return (
        <div className="flex flex-wrap gap-2">
            {emails.slice().sort((a, b) => (a.ranking ?? 99) - (b.ranking ?? 99)).map((e, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700">
                    <span className="text-sm text-slate-700 dark:text-slate-200">{e.email}</span>
                    {e.ranking === 1 && <span className="px-1.5 py-0.5 text-[9px] font-bold bg-primary/10 text-primary rounded">melhor</span>}
                </div>
            ))}
        </div>
    );
};

const Enderecos: React.FC<{ ends: LemitEndereco[] }> = ({ ends }) => {
    if (!ends.length) return <EmptyRow>Nenhum endereço retornado</EmptyRow>;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ends.map((e, i) => (
                <div key={i} className="px-3 py-2.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700 text-sm">
                    <p className="font-semibold text-slate-700 dark:text-slate-200">{e?.endereco || [e?.logradouro, e?.numero].filter(Boolean).join(', ') || '—'}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        {[e?.bairro, e?.cidade && e?.uf ? `${e.cidade}/${e.uf}` : e?.cidade, e?.cep].filter(Boolean).join(' · ')}
                    </p>
                </div>
            ))}
        </div>
    );
};

// ── Adaptador: enriquecimento_data (Lista Fria) → shape padrão da Lemit ─────────
export interface EnrLike {
    empresa?: { telefones?: LemitTelefone[]; emails?: LemitEmail[]; endereco?: LemitEndereco | null };
    pessoa?: { nome?: string; data_nascimento?: string; telefones?: LemitTelefone[]; emails?: LemitEmail[]; endereco?: LemitEndereco | null };
    socios?: LemitSocio[];
    socios_enriquecidos?: Array<{ cpf: string; nome?: string; pessoa?: LemitPessoaData }>;
    socio_principal?: { cpf: string; nome?: string; participacao?: number };
    representante?: { nome?: string; cpf?: string; data_nascimento?: string };
    representante_pendente?: boolean;
    melhor_whatsapp?: string;
    melhor_email?: string;
}

/** Constrói um objeto no formato LemitPessoaData/EmpresaData a partir do enriquecimento encenado. */
export function enrToLemitLike(
    enr: EnrLike | null | undefined,
    tipo: LemitTipo,
    documento: string,
    nomeFallback?: string,
): LemitPessoaData | LemitEmpresaData {
    if (!enr) return tipo === 'empresa' ? { cnpj: documento, razao_social: nomeFallback } : { cpf: documento, nome: nomeFallback };
    if (tipo === 'empresa') {
        return {
            cnpj: documento,
            razao_social: nomeFallback,
            celulares: enr.empresa?.telefones || [],
            fixos: [],
            emails: enr.empresa?.emails || [],
            endereco: enr.empresa?.endereco || null,
            socios: enr.socios || [],
        } as LemitEmpresaData;
    }
    return {
        cpf: documento,
        nome: enr.pessoa?.nome || nomeFallback,
        data_nascimento: enr.pessoa?.data_nascimento,
        celulares: enr.pessoa?.telefones || [],
        fixos: [],
        emails: enr.pessoa?.emails || [],
        enderecos: enr.pessoa?.endereco ? [enr.pessoa.endereco] : [],
    } as LemitPessoaData;
}

/** Dados do CPF do representante já salvos no enriquecimento da Lista Fria. */
export function socioPessoaFromEnr(enr: EnrLike | null | undefined): LemitPessoaData | null {
    const s = enr?.socios_enriquecidos?.[0];
    return (s?.pessoa as LemitPessoaData) || null;
}

interface Props {
    data: LemitPessoaData | LemitEmpresaData;
    tipo: LemitTipo;
    /** Destaque do sócio majoritário / representante legal (dono do lead PJ). */
    highlight?: { socioPrincipalCpf?: string; representante?: { nome?: string; cpf?: string; data_nascimento?: string } };
    /** Retorno da Lemit para o CPF do representante — vira seção própria. */
    socioData?: LemitPessoaData | null;
    /** Há 2+ sócios e ninguém definido: mostra o aviso + botão de escolher. */
    representantePendente?: boolean;
    onEscolherRepresentante?: () => void;
    /** Consulta o CPF do representante (paga) quando os dados dele ainda não existem. */
    onConsultarSocio?: () => void;
    consultandoSocio?: boolean;
    custoPessoa?: number | null;
}

const EnriquecimentoPanel: React.FC<Props> = ({ data, tipo, highlight, socioData, representantePendente, onEscolherRepresentante, onConsultarSocio, consultandoSocio, custoPessoa }) => {
    const isEmpresa = tipo === 'empresa';
    const empresa = isEmpresa ? (data as LemitEmpresaData) : null;
    const pessoa = !isEmpresa ? (data as LemitPessoaData) : null;

    const tels: LemitTelefone[] = [...((data as any).celulares || []), ...((data as any).fixos || [])];
    const emails: LemitEmail[] = ((data as any).emails || []);
    const ends: LemitEndereco[] = isEmpresa ? (empresa?.endereco ? [empresa.endereco] : []) : (pessoa?.enderecos || []);
    const socios = empresa?.socios || [];
    const principalCpf = (highlight?.socioPrincipalCpf || '').replace(/\D/g, '');
    const rep = highlight?.representante;

    const socioTels: LemitTelefone[] = socioData ? [...(socioData.celulares || []), ...(socioData.fixos || [])] : [];

    return (
        <div className="space-y-3">
            {/* Precisa escolher o representante (2+ sócios) */}
            {representantePendente && (
                <div className="flex items-center gap-3 rounded-2xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3.5">
                    <AlertTriangle size={18} className="text-amber-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-amber-800 dark:text-amber-300">Esta empresa tem {socios.length} sócios</p>
                        <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80">Escolha quem é o representante legal — não dá para definir sozinho.</p>
                    </div>
                    {onEscolherRepresentante && (
                        <button onClick={onEscolherRepresentante}
                            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl bg-amber-500 text-white hover:bg-amber-600 shrink-0">
                            <UserCog size={13} /> Escolher
                        </button>
                    )}
                </div>
            )}

            {/* Representante legal / dono */}
            {rep && (rep.nome || rep.cpf) && (
                <div className="rounded-2xl border border-amber-200 dark:border-amber-800/40 bg-amber-50/70 dark:bg-amber-900/10 p-3.5">
                    <div className="flex items-center gap-2 mb-1.5">
                        <Crown size={15} className="text-amber-600" />
                        <span className="text-xs font-extrabold uppercase tracking-wider text-amber-700 dark:text-amber-400">Representante legal / dono</span>
                        {onEscolherRepresentante && socios.length > 1 && (
                            <button onClick={onEscolherRepresentante} className="ml-auto text-[11px] font-bold text-amber-700 dark:text-amber-400 hover:underline">Trocar</button>
                        )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <InfoCell label="Nome" value={rep.nome} />
                        <InfoCell label="CPF" value={fmtDoc(rep.cpf)} />
                        {rep.data_nascimento && <InfoCell label="Nascimento" value={fmtDate(rep.data_nascimento)} />}
                    </div>
                </div>
            )}

            {/* Dados cadastrais da empresa/pessoa */}
            <SectionCard icon={isEmpresa ? <Building2 size={14} /> : <User size={14} />} title={isEmpresa ? 'Dados cadastrais da empresa' : 'Dados cadastrais'} tint="text-blue-700 bg-blue-50 dark:bg-blue-900/15 dark:text-blue-400">
                {isEmpresa ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <InfoCell label="Razão social" value={empresa?.razao_social} />
                        <InfoCell label="Nome fantasia" value={empresa?.nome_fantasia} />
                        <InfoCell label="CNPJ" value={fmtDoc(empresa?.cnpj)} />
                        <InfoCell label="Situação" value={empresa?.situacao} />
                        <InfoCell label="Fundação" value={fmtDate(empresa?.data_fundacao)} />
                        <InfoCell label="CNAE" value={empresa?.cnae ? `${empresa.cnae.numero || ''} ${empresa.cnae.descricao || ''}`.trim() : '—'} />
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <InfoCell label="Nome" value={pessoa?.nome} />
                        <InfoCell label="CPF" value={fmtDoc(pessoa?.cpf)} />
                        <InfoCell label="Nascimento" value={fmtDate(pessoa?.data_nascimento)} />
                        <InfoCell label="Situação CPF" value={pessoa?.situacao_cpf} />
                        <InfoCell label="Renda estimada" value={pessoa?.renda != null ? fmtBRL(pessoa.renda) : '—'} />
                        <InfoCell label="Ocupação" value={pessoa?.ocupacao} />
                    </div>
                )}
            </SectionCard>

            <SectionCard icon={<Phone size={14} />} title={isEmpresa ? 'Telefones da empresa' : 'Telefones'} count={tels.length} tint="text-emerald-700 bg-emerald-50 dark:bg-emerald-900/15 dark:text-emerald-400">
                <Telefones tels={tels} />
            </SectionCard>

            <SectionCard icon={<Mail size={14} />} title={isEmpresa ? 'E-mails da empresa' : 'E-mails'} count={emails.length} tint="text-sky-700 bg-sky-50 dark:bg-sky-900/15 dark:text-sky-400">
                <Emails emails={emails} />
            </SectionCard>

            <SectionCard icon={<MapPin size={14} />} title="Endereços" count={ends.length} tint="text-rose-700 bg-rose-50 dark:bg-rose-900/15 dark:text-rose-400">
                <Enderecos ends={ends} />
            </SectionCard>

            {/* Quadro societário (empresa) — destaca o sócio principal */}
            {isEmpresa && socios.length > 0 && (
                <SectionCard icon={<Users size={14} />} title="Quadro societário" count={socios.length} tint="text-violet-700 bg-violet-50 dark:bg-violet-900/15 dark:text-violet-400">
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead><tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                <th className="px-3 py-2">Nome</th><th className="px-3 py-2">CPF</th><th className="px-3 py-2 text-center">Participação</th>
                            </tr></thead>
                            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                {socios.slice().sort((a, b) => (b.participacao ?? 0) - (a.participacao ?? 0)).map((s, i) => {
                                    const isPrincipal = principalCpf && (s.cpf || '').replace(/\D/g, '') === principalCpf;
                                    return (
                                        <tr key={i} className={isPrincipal ? 'bg-amber-50/60 dark:bg-amber-900/10' : ''}>
                                            <td className="px-3 py-2 font-bold text-slate-700 dark:text-slate-200">
                                                <span className="inline-flex items-center gap-1.5">
                                                    {isPrincipal && <Crown size={12} className="text-amber-500" />}{s.nome}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 font-mono text-slate-500">{fmtDoc(s.cpf)}</td>
                                            <td className="px-3 py-2 text-center font-bold text-primary">{s.participacao != null ? `${s.participacao}%` : '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </SectionCard>
            )}

            {/* CPF do representante conhecido mas NUNCA consultado — explica a ausência dos dados */}
            {!socioData && rep?.cpf && (
                <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/30 p-4">
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-slate-200/70 dark:bg-slate-700 text-slate-500"><User size={15} /></div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Dados pessoais do representante não consultados</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                Sabemos <b>quem</b> é o dono (veio do quadro societário, de graça), mas o telefone, e-mail e endereço
                                pessoais dele exigem uma consulta separada do CPF {fmtDoc(rep.cpf)}.
                            </p>
                            {onConsultarSocio && (
                                <button
                                    onClick={onConsultarSocio}
                                    disabled={consultandoSocio}
                                    className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl bg-[#5F6C37] text-white hover:bg-[#4d5a2c] disabled:opacity-50"
                                >
                                    {consultandoSocio ? <Loader2 size={13} className="animate-spin" /> : <Crown size={13} />}
                                    {consultandoSocio ? 'Consultando…' : 'Buscar dados do dono'}
                                    {custoPessoa != null && !consultandoSocio && (
                                        <span className="opacity-75">({custoPessoa.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })})</span>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* O que a Lemit extraiu do CPF do representante */}
            {socioData && (
                <div className="rounded-2xl border-2 border-amber-200 dark:border-amber-800/40 p-3 space-y-3 bg-amber-50/30 dark:bg-amber-900/5">
                    <div className="flex items-center gap-2 px-1">
                        <Crown size={14} className="text-amber-600" />
                        <h3 className="text-xs font-extrabold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                            Dados do representante (CPF {fmtDoc(socioData.cpf)})
                        </h3>
                    </div>

                    <SectionCard icon={<User size={14} />} title="Dados cadastrais do representante" tint="text-blue-700 bg-blue-50 dark:bg-blue-900/15 dark:text-blue-400">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <InfoCell label="Nome" value={socioData.nome} />
                            <InfoCell label="CPF" value={fmtDoc(socioData.cpf)} />
                            <InfoCell label="Nascimento" value={fmtDate(socioData.data_nascimento)} />
                            <InfoCell label="Situação CPF" value={socioData.situacao_cpf} />
                            <InfoCell label="Renda estimada" value={socioData.renda != null ? fmtBRL(socioData.renda) : '—'} />
                            <InfoCell label="Ocupação" value={socioData.ocupacao} />
                            {socioData.nome_mae && <InfoCell label="Nome da mãe" value={socioData.nome_mae} />}
                            {socioData.risco_credito?.score_credito && <InfoCell label="Score de crédito" value={socioData.risco_credito.score_credito} />}
                        </div>
                    </SectionCard>

                    <SectionCard icon={<Phone size={14} />} title="Telefones do representante" count={socioTels.length} tint="text-emerald-700 bg-emerald-50 dark:bg-emerald-900/15 dark:text-emerald-400">
                        <Telefones tels={socioTels} />
                    </SectionCard>

                    <SectionCard icon={<Mail size={14} />} title="E-mails do representante" count={(socioData.emails || []).length} tint="text-sky-700 bg-sky-50 dark:bg-sky-900/15 dark:text-sky-400">
                        <Emails emails={socioData.emails || []} />
                    </SectionCard>

                    <SectionCard icon={<MapPin size={14} />} title="Endereços do representante" count={(socioData.enderecos || []).length} tint="text-rose-700 bg-rose-50 dark:bg-rose-900/15 dark:text-rose-400">
                        <Enderecos ends={socioData.enderecos || []} />
                    </SectionCard>

                    {(socioData.participacao_societaria || []).length > 0 && (
                        <SectionCard icon={<Building2 size={14} />} title="Outras empresas do representante" count={socioData.participacao_societaria!.length} tint="text-violet-700 bg-violet-50 dark:bg-violet-900/15 dark:text-violet-400">
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead><tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                        <th className="px-3 py-2">Empresa</th><th className="px-3 py-2">CNPJ</th><th className="px-3 py-2 text-center">Part.</th><th className="px-3 py-2 text-center">Situação</th>
                                    </tr></thead>
                                    <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                        {socioData.participacao_societaria!.map((p, i) => (
                                            <tr key={i}>
                                                <td className="px-3 py-2 font-bold text-slate-700 dark:text-slate-200">{p.nome}</td>
                                                <td className="px-3 py-2 font-mono text-slate-500">{fmtDoc(p.cnpj)}</td>
                                                <td className="px-3 py-2 text-center font-bold text-primary">{p.participacao_socio != null ? `${p.participacao_socio}%` : '—'}</td>
                                                <td className="px-3 py-2 text-center text-slate-500">{p.situacao_cadastral || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </SectionCard>
                    )}
                </div>
            )}
        </div>
    );
};

export default EnriquecimentoPanel;
